import React, { useRef, useImperativeHandle, forwardRef, useMemo, useEffect, useLayoutEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { usePageLayout } from '../../assets/contexts/PageLayoutContext';
import { resolveActiveTopMenuIndex } from '../../assets/utils/comm/activeTopMenuFromBreadcrumb';
import { useFixedWithMoreLayout } from './useFixedWithMoreLayout';

/**
 * 메인 메뉴 컴포넌트
 * 역할: 최상위 메뉴 (level 0) - 헤더 영역 렌더링
 */
const MainMenu = forwardRef(({ menuData, themeConfig = {} }, ref) => {
  const mainMenuRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { bannerData } = usePageLayout();

  const {
    mainMenuNavRef,
    moreContainerRef,
    useFixedWithMore,
    showScrollButtons,
    visibleCountToUse,
    visibleItems,
    overflowItems,
    moreOpen,
    setMoreOpen,
    setNeedReduceForLogo,
  } = useFixedWithMoreLayout(menuData, themeConfig);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (moreContainerRef.current && !moreContainerRef.current.contains(e.target)) {
        setMoreOpen(false);
      }
    };
    if (moreOpen) {
      document.addEventListener('click', handleClickOutside, true);
    }
    return () => document.removeEventListener('click', handleClickOutside, true);
  }, [moreOpen, moreContainerRef, setMoreOpen]);

  // 더보기 펼침 시 로고 영역과 0번째 메뉴 항목 겹침 여부 측정 → 겹칠 때만 needReduceForLogo
  useLayoutEffect(() => {
    if (!useFixedWithMore || !moreOpen || !mainMenuRef.current || !mainMenuNavRef.current) return;
    const headerBottom = mainMenuRef.current.closest('.header-bottom');
    const logoEl = headerBottom?.querySelector('.logo');
    const ul = mainMenuNavRef.current.querySelector(':scope > ul.main-menu-list');
    const firstLi = ul?.querySelector(':scope > li:first-child');
    if (!logoEl || !firstLi) return;
    const logoRect = logoEl.getBoundingClientRect();
    const firstRect = firstLi.getBoundingClientRect();
    const gap = 8;
    const overlaps = firstRect.left < logoRect.right + gap;
    setNeedReduceForLogo((prev) => (overlaps ? true : prev));
  }, [useFixedWithMore, moreOpen, visibleCountToUse, mainMenuNavRef, setNeedReduceForLogo]);

  useImperativeHandle(ref, () => ({
    mainMenuRef: mainMenuRef.current,
    mainMenuNavRef: mainMenuNavRef.current
  }));

  // 현재 경로 → 없으면 제목배너 breadcrumb으로 최상위 메뉴(1depth) 인덱스
  const activeTopMenuIndex = useMemo(
    () => resolveActiveTopMenuIndex(menuData, location.pathname, bannerData?.breadcrumbPath),
    [menuData, location.pathname, bannerData?.breadcrumbPath]
  );

  // href를 React Router 경로로 변환하는 헬퍼 함수
  const convertToReactRoute = (href) => {
    if (!href || href === '#') return null;

    // 외부 URL인 경우 (http://, https://, //)
    if (/^(https?:)?\/\//.test(href)) {
      return null; // 외부 링크는 그대로 사용
    }

    // React Router 경로로 변환
    return href;
  };

  const handleMenuClick = (e, href) => {
    // 원본 JavaScript 이벤트 리스너가 실행되지 않도록 먼저 처리
    e.preventDefault();
    e.stopPropagation();
    if (e.nativeEvent) {
      e.nativeEvent.stopImmediatePropagation();
    }

    const route = convertToReactRoute(href);

    if (route) {
      navigate(route);
    } else if (href) {
      // 외부 링크인 경우
      window.location.href = href;
    }
  };

  return (
    <div className="main-menu text-center" ref={mainMenuRef}>
      {showScrollButtons ? (
        <button
          type="button"
          className="main-menu-scroll-btn main-menu-scroll-btn-left"
          aria-label="왼쪽으로 스크롤"
        >
          <i className="pe-7s-angle-left"></i>
        </button>
      ) : null}
      <nav ref={mainMenuNavRef}>
        {menuData && menuData.length > 0 ? (
          <>
            <ul className="main-menu-list">
              {visibleItems.map((item, index) => {
                const href = item.href && item.href !== '#' ? item.href : null;
                const route = convertToReactRoute(href);
                const isActive = activeTopMenuIndex === index;

                return (
                  <li
                    key={index}
                    className={`main-menu-item ${isActive ? 'active' : ''}`}
                    data-menu-index={index}
                  >
                    {route ? (
                      <a
                        href={route}
                        onClick={(e) => handleMenuClick(e, href)}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                        }}
                      >
                        {item.label}
                      </a>
                    ) : href ? (
                      <a
                        href={href}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {item.label}
                      </a>
                    ) : (
                      // eslint-disable-next-line jsx-a11y/anchor-is-valid -- 메뉴 항목(링크 없음)은 클릭만 처리
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                      >
                        {item.label}
                      </a>
                    )}
                  </li>
                );
              })}
              {useFixedWithMore && overflowItems.length > 0 && (
                <li
                  className={`main-menu-item main-menu-more${activeTopMenuIndex != null && activeTopMenuIndex >= visibleCountToUse ? ' main-menu-more--active-in-dropdown' : ''}`}
                  ref={moreContainerRef}
                >
                  <button
                    type="button"
                    className="main-menu-more-trigger"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setMoreOpen((prev) => !prev);
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    aria-expanded={moreOpen}
                    aria-haspopup="true"
                    aria-label="더보기"
                  >
                    <i className="pe-7s-angle-down" aria-hidden="true" />
                  </button>
                  <div className={`more-dropdown ${moreOpen ? 'is-open' : ''}`}>
                    <ul className="more-dropdown-list main-menu-list">
                      {overflowItems.map((item, i) => {
                        const index = visibleCountToUse + i;
                        const href = item.href && item.href !== '#' ? item.href : null;
                        const route = convertToReactRoute(href);
                        const isActive = activeTopMenuIndex === index;

                        return (
                          <li
                            key={index}
                            className={`main-menu-item ${isActive ? 'active' : ''}`}
                            data-menu-index={index}
                          >
                            {route ? (
                              <a
                                href={route}
                                onClick={(e) => {
                                  handleMenuClick(e, href);
                                  setMoreOpen(false);
                                }}
                                onMouseDown={(e) => e.stopPropagation()}
                              >
                                {item.label}
                              </a>
                            ) : href ? (
                              <a
                                href={href}
                                onClick={() => setMoreOpen(false)}
                              >
                                {item.label}
                              </a>
                            ) : (
                              // eslint-disable-next-line jsx-a11y/anchor-is-valid -- 더보기 메뉴 항목(링크 없음)
                              <a
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                }}
                              >
                                {item.label}
                              </a>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </li>
              )}
            </ul>
          </>
        ) : (
          <ul className="main-menu-list">
            <li className="main-menu-item">
              <Link to="/">HOME</Link>
            </li>
          </ul>
        )}
      </nav>
      {showScrollButtons ? (
        <button
          type="button"
          className="main-menu-scroll-btn main-menu-scroll-btn-right"
          aria-label="오른쪽으로 스크롤"
        >
          <i className="pe-7s-angle-right"></i>
        </button>
      ) : null}
    </div>
  );
});

MainMenu.displayName = 'MainMenu';

export default MainMenu;

